import {
  Box,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import NoData from "../../../components/UI/NoData";
import useFetch from "../../../hooks/useFetch";
import {
  ICalendarIdentifiers,
  IDoctorAgenda,
} from "../../../models/doctor-agenda";
import DoctorAgendaService from "../../../services/doctorAgenda.service";
import AppointmentCard from "../AppointmentCard";
import ScheduleTypeIcon, { SCHEDULE_TYPE_LIST } from "../ScheduleTypeIcon";
import { useDoctorAgenda } from "./useDoctorAgenda";
import { CalendarMonth } from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useTranslation } from "react-i18next";
import { InfoButton } from "../InfoButton";
import dayjs from "dayjs";

const translation_path = "page.doctorAgenda.";

const doctorAgendaService = new DoctorAgendaService();

interface IDoctorAgendaListProps {
  calendarDateStart: Date;
  calendarDateEnd: Date;
  isCustomDate?: boolean;
  onDateChange?: (date: dayjs.Dayjs | null) => void;
  showFree?: boolean;
}

export const DoctorAgendaList: React.FC<IDoctorAgendaListProps> = memo(
  ({
    calendarDateStart,
    calendarDateEnd,
    isCustomDate,
    onDateChange,
    showFree,
  }) => {
    const { t, i18n } = useTranslation();

    const { sendRequest, loading: dloading } = useFetch(
      doctorAgendaService.getAll
    );

    const { getBrazilianDateISO, dayjsBrazil, icons } = useDoctorAgenda();

    const [appointments, setAppointments] = useState<IDoctorAgenda[]>([]);

    const [calendarIdentifiers, setCalendarIdentifiers] = useState<
      ICalendarIdentifiers[]
    >([]);

    const [loading, setloading] = useState(true);

    const formattedDate = useMemo(
      () => dayjsBrazil(calendarDateStart),
      [calendarDateStart, dayjsBrazil]
    );

    const fetchAppointments = useCallback(async () => {
      const { data, success } = await sendRequest({
        CalendarDateStart: getBrazilianDateISO(calendarDateStart),
        CalendarDateEnd: getBrazilianDateISO(calendarDateEnd),
        ShowFree: showFree,
      });
      if (success && data) {
        const sortedData = data.sort((a, b) => {
          const dateA = new Date(a.calendarStartDate);
          const dateB = new Date(b.calendarStartDate);
          return dateA.getTime() - dateB.getTime();
        });
        const identifiersMap = new Map<string | number, ICalendarIdentifiers>();
        sortedData.forEach((item) => {
          const newItem = {
            calendarIdentifier: item.calendarIdentifier,
            legend: item.calendarShortName || item.descriptionSite,
          };
          identifiersMap.set(item.calendarIdentifier, newItem);
        });
        const uniqueCalendarIdentifiers = Array.from(identifiersMap.values());

        setAppointments(sortedData);
        setCalendarIdentifiers(uniqueCalendarIdentifiers);
      }
      setloading(false);
    }, [
      sendRequest,
      getBrazilianDateISO,
      calendarDateStart,
      calendarDateEnd,
      showFree,
    ]);

    useEffect(() => {
      fetchAppointments();
    }, [fetchAppointments]);

    if (loading || dloading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            py: 5,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Stack spacing={2}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {isCustomDate ? (
            <Box maxWidth={250}>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale={i18n.language}
              >
                <DatePicker
                  label={t(translation_path + "select_date")}
                  value={formattedDate}
                  onChange={(newValue) => onDateChange!(newValue)}
                  format="DD/MM/YYYY"
                />
              </LocalizationProvider>
            </Box>
          ) : (
            <Box
              display="inline-flex"
              alignItems="center"
              sx={{
                alignSelf: "flex-start",
                backgroundColor: "transparent",
                padding: 1.5,
                borderRadius: 20,
                borderWidth: 1,
                borderColor: "primary.main",
                borderStyle: "solid",
              }}
            >
              <CalendarMonth color="primary" />
              <Typography sx={{ fontSize: 16, fontWeight: "bold", ml: 1 }}>
                {formattedDate.format("DD/MM/YYYY")}
              </Typography>
            </Box>
          )}

          <InfoButton
            title={t(translation_path + "info_title")}
            content={
              <>
                {SCHEDULE_TYPE_LIST.map((type) => (
                  <Box
                    key={type.code}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      paddingY: 1,
                    }}
                  >
                    <ScheduleTypeIcon
                      tasyScheduleTypeCode={type.code}
                      size={32}
                      disableTooltip
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        marginLeft: 2,
                        fontSize: 16,
                        color: "text.primary",
                        flexShrink: 1,
                      }}
                    >
                      {t(
                        translation_path +
                          "scheduleType." +
                          type.translationKey
                      )}
                    </Typography>
                  </Box>
                ))}

                <Divider sx={{ marginY: 1 }} />

                {calendarIdentifiers.length > 0 ? (
                  calendarIdentifiers.map((identifier, index) => {
                    const icon = icons[index];
                    if (!icon) return null;

                    return (
                      <Box
                        key={identifier.calendarIdentifier}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          paddingY: 1,
                        }}
                      >
                        {icon}
                        <Typography
                          variant="body1"
                          sx={{
                            marginLeft: 2,
                            fontSize: 16,
                            color: "text.primary",
                            flexShrink: 1,
                          }}
                        >
                          {identifier.legend ||
                            t(translation_path + "not_informed")}
                        </Typography>
                      </Box>
                    );
                  })
                ) : (
                  <Typography
                    variant="body1"
                    sx={{
                      marginLeft: 2,
                      fontSize: 16,
                      color: "text.primary",
                      flexShrink: 1,
                    }}
                  >
                    {t(translation_path + "no_legend")}
                  </Typography>
                )}
              </>
            }
          />
        </Box>

        {appointments.length > 0 ? (
          appointments.map((item) => {
            const appointmentDate = new Date(item.calendarStartDate);
            const hour = appointmentDate.toLocaleTimeString("pt-BR", {
              hour: "2-digit",
              minute: "2-digit",
            });
            const iconPosition = calendarIdentifiers.findIndex(
              (identifier) =>
                identifier.calendarIdentifier === item.calendarIdentifier
            );
            const icon = icons[iconPosition];

            return (
              <AppointmentCard
                key={item.identifier}
                appointment={item}
                icon={icon}
                hour={hour}
              />
            );
          })
        ) : (
          <NoData message={t(translation_path + "no_appointment")} />
        )}
      </Stack>
    );
  }
);

export default DoctorAgendaList;
